// V2 版式：cover（前端 DOM 版）——对照后端 _shared/pptx/v2/layouts/cover.ts 逐坐标移植
// 瑞士：accent 满屏 + 细体大字 + 等宽 meta 行；杂志：paper 底 + 衬线巨字 + 期刊页眉。
// 轻奢：墨黑满屏 + 裸金装饰圆 + 白衬线巨字 + 裸金 hairline（bg=accent 时按深底页取色，不走 accentOn）。
// 主题库扩展 6 风格（044 T3.3）：consult 双段 rule（navy+emerald）/ brutal 红短线+全大写标题 /
// term `>` 提示符 / report 红短线+SOURCES 来源行 / ink 朱印落款 / memphis 边距母题。

import { SPACING } from "../../lib/tokens.generated";
import { px, pt, titleSize } from "./shared";
import { isLuxe, LuxeDecorCircle } from "./luxe";
import {
  isConsult, isBrutal, isTerm, isReport,
  consultTheme, brutalTheme, reportTheme,
  InkSeal, MemphisMotif,
} from "./themes";
import { EditableText } from "../SlideCanvas";
import type { V2LayoutProps } from "./V2Slide";

export default function V2Cover({ c, skin, editable, handlers }: V2LayoutProps) {
  const m = SPACING.marginX;
  const luxe = isLuxe(skin);
  const onAccent = skin.bg === "accent" && !luxe;
  const mag = skin.style === "magazine";
  const W = 13.333;
  // 6 新风格封面在 skin 层均映射为深底满屏——meta 行按深底页取色（cover.ts onDeep 分支）
  const consult = isConsult(skin);
  const brutal = isBrutal(skin);
  const term = isTerm(skin);
  const report = isReport(skin);
  const onDeep = onAccent && !consult && !brutal && !term && !report && !isLuxe(skin) &&
    skin.style !== "ink" && skin.style !== "memphis";
  const consultT = consultTheme(skin);
  const brutalT = brutalTheme(skin);
  const reportT = reportTheme(skin);
  // 顶部 rule 色：luxe 裸金 / report 琥珀 / 深底页 accentOn / 其余 hairline（cover.ts 分支）
  const line = consultT ? "" : onDeep ? skin.accentOn : reportT ? reportT.accent : luxe ? skin.accent : skin.hairline;
  const lineBottom = onDeep ? skin.accentOn : reportT ? reportT.muted : luxe ? skin.accent : skin.hairline;
  const metaColor = onDeep ? skin.accentOn : skin.sub;

  return (
    <>
      {/* 裸金装饰圆（仅 luxe；先画，文字压其上） */}
      <LuxeDecorCircle skin={skin} variant="cover" />
      {/* memphis（043 §4.7）：封面深底满屏上母题狂欢（边距内，绝不压内容区） */}
      <MemphisMotif skin={skin} variant="cover" />
      {/* 顶部 meta 行 + rule */}
      <div style={{
        position: "absolute", left: px(m), top: px(0.55), width: px(8), height: px(0.3),
        fontSize: pt(skin.scale.meta), color: metaColor,
        fontFamily: skin.fontMono, letterSpacing: "0.25em", textTransform: "uppercase",
      }}>{mag ? `MARKETING PLAN · ${new Date().getFullYear()}` : (term ? "> " : "") + "COVER"}</div>
      {consultT ? (
        /* consult（043 §4.2）：顶部 signature rule 降级为双段实色——navy 主段 + emerald 尾段 */
        <>
          <div style={{
            position: "absolute", left: px(m), top: px(1.0), width: px((W - 2 * m) * 0.85), height: 1,
            background: skin.bg === "paper" ? consultT.ink : skin.text,
          }} />
          <div style={{
            position: "absolute", left: px(m + (W - 2 * m) * 0.85), top: px(1.0),
            width: px((W - 2 * m) * 0.15), height: 1, background: consultT.accentEmerald,
          }} />
        </>
      ) : (
        <div style={{
          position: "absolute", left: px(m), top: px(1.0), width: px(W - 2 * m), height: 1,
          background: line,
        }} />
      )}

      {/* brutal（043 §4.3）/report（043 §4.5）：标题上方红色短粗线——本页唯一红 */}
      {(brutalT || reportT) && (
        <div style={{
          position: "absolute", left: px(m), top: px(2.15), width: px(1.2),
          height: brutalT ? px(0.06) : px(0.05),
          background: reportT ? reportT.accentRed : brutalT!.accent,
        }} />
      )}

      {/* 主标题：brutal 全大写走 CSS textTransform（045 #11：不进可编辑值，防编辑把大写写回存储）；
          term `>` 前缀在持久化前剥离 */}
      <EditableText
        value={(term ? "> " : "") + c.title} path="title" editable={editable} multiline
        onEdit={(p, v) => handlers.onEdit?.(p, term && v.startsWith("> ") ? v.slice(2) : v)}
        onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
        style={{
          position: "absolute", left: px(m), top: px(2.35), width: px(W - 2 * m), height: px(1.9),
          fontSize: pt(titleSize(c.title, skin.scale.coverTitle)), color: skin.text,
          fontFamily: skin.fontHead, fontWeight: mag ? 700 : 400, lineHeight: 1.1,
          textTransform: brutal ? "uppercase" : undefined,
        }}
      />

      {/* 副标题 */}
      {(c.subtitle || editable) && (
        <EditableText
          value={c.subtitle ?? ""} path="subtitle" editable={editable}
          onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
          style={{
            position: "absolute", left: px(m), top: px(4.45), width: px(W - 2 * m - 2), height: px(0.6),
            fontSize: pt(skin.scale.sectionHead), color: metaColor,
            fontFamily: skin.fontBody,
          }}
        />
      )}

      {/* 底部 meta（term `$ ` 提示符 / report SOURCES 来源行） */}
      <div style={{
        position: "absolute", left: px(m), top: px(6.35), width: px(W - 2 * m), height: 1,
        background: lineBottom,
      }} />
      <div style={{
        position: "absolute", left: px(m), top: px(6.5), width: px(W - 2 * m), height: px(0.3),
        fontSize: pt(skin.scale.meta), color: metaColor,
        fontFamily: skin.fontMono, letterSpacing: "0.15em",
      }}>{(term ? "$ " : reportT ? "SOURCES: " : "") + "策略咖 · MARKETING PLAN"}</div>

      {/* ink（043 §4.6）：朱红印章落款——右下角小方块章，白字「印」 */}
      <InkSeal skin={skin} x={W - m - 0.62} y={5.4} />
    </>
  );
}
